import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { getEmployees } from "../services/employeeService";

const EmployeesContext = createContext({
  employees: [],
  loading: true,
  error: "",
  refreshEmployees: async () => {},
});

// Shared cache for GET api/employees/?search=&role_name= so pages/forms that
// need the full employee list (dropdowns, assignees, etc.) don't each fetch it
// separately. Pages with role-scoped queries (manager_id) keep their own fetch.
export function EmployeesProvider({ children }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refreshEmployees = useCallback(async ({ search = "", roleName = "" } = {}) => {
    try {
      setLoading(true);
      setError("");

      const data = await getEmployees(search, null, roleName);
      setEmployees(data);
    } catch (requestError) {
      console.error("Employee Fetch Error:", requestError);
      setError("Unable to fetch employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshEmployees();
  }, [refreshEmployees]);

  const value = useMemo(
    () => ({ employees, loading, error, refreshEmployees }),
    [employees, loading, error, refreshEmployees]
  );

  return (
    <EmployeesContext.Provider value={value}>
      {children}
    </EmployeesContext.Provider>
  );
}

export function useEmployees() {
  return useContext(EmployeesContext);
}
